import React, { useState, useRef, useMemo } from 'react';
import PropTypes from 'prop-types';
import { useAppSelector } from 'careand-redux/store';
import { useAppDispatch } from 'careand-redux/useAppDispatch';
import { connect } from 'react-redux';
import Icon from 'components/Utils/NewIcon/index';
import CalendarUtils from 'services/CalendarUtils';
import PhysicianUtils from 'services/PhysicianUtils';
import AppointmentDetails from 'components/HealthRecords/Appointments/AppointmentDetails';
import DefaultModal from 'components/Utils/DefaultModal';
import AppointmentUtils from 'services/AppointmentUtils';
import { api } from 'careand-redux/api/ApiThunks';
import { TranscriptionChat } from 'components/Video/Jitsi/JitsiMeet';
import { AppointmentBriefing } from 'components/Patient/CPP/CPPTimeline';

const CPPTimelineAppointment = (props) => {
  const dispatch = useAppDispatch();
  const authentication = useAppSelector((state) => state.authentication);
  const [transcription, setTranscription] = useState();
  const downloadingTranscription = useRef(false);
  const [showTranscriptionModal, setShowTranscriptionModal] = useState(false);
  const [showBriefingModal, setShowBriefingModal] = useState(false);
  const [openModal, setOpenModal] = useState(false);

  const ownId = authentication.user.id;
  const ownPermissions = authentication.permissions;

  const { appointment, physicians } = props;
  const { date, physician_id, status } = appointment;
  const physician = physicians[physician_id];
  const ownUser = physicians[ownId];

  const transcriptionAvailable = AppointmentUtils.isTranscriptionAvailable(
    appointment,
    ownUser,
    ownPermissions,
  );

  function onCardHover() {
    if (transcription || !transcriptionAvailable || downloadingTranscription.current) {
      return;
    }

    downloadingTranscription.current = true;
    dispatch(
      api.Appointments.getAppointmentTranscription({
        id: props.appointment.id,
      }),
    )
      .then((transcription) => {
        setTranscription(transcription);
      })
      .finally(() => {
        downloadingTranscription.current = false;
      });
  }

  return (
    <>
      {showBriefingModal && (
        <BriefingModal
          isOpen={showBriefingModal}
          onRequestClose={() => setShowBriefingModal(false)}
          appointment={appointment}
        />
      )}
      {showTranscriptionModal && (
        <TranscriptionModal
          isOpen={showTranscriptionModal}
          onRequestClose={() => setShowTranscriptionModal(false)}
          transcription={transcription}
        />
      )}
      {openModal && (
        <AppointmentDetailsModal
          appointment={appointment}
          openModal={openModal}
          setOpenModal={setOpenModal}
        />
      )}
      <div
        className="item-wrapper clickable"
        onClick={() => {
          setOpenModal(true);
        }}
        onMouseOver={onCardHover}
      >
        <div className="hover-icons">
          {appointment.ai_briefing && (
            <Icon
              src="sparkle"
              alt="Open Carey Briefing"
              showTooltip
              width={15}
              onClick={(ev) => {
                ev.stopPropagation();
                ev.nativeEvent.stopImmediatePropagation();
                setShowBriefingModal(true);
              }}
            />
          )}
          {transcription && transcriptionAvailable && (
            <Icon
              src="message"
              alt="Open Transcription"
              showTooltip
              width={15}
              onClick={(ev) => {
                ev.stopPropagation();
                ev.nativeEvent.stopImmediatePropagation();
                setShowTranscriptionModal(true);
              }}
            />
          )}
        </div>
        <div className="top-bar">
          <Icon src="calendar-clock" />
          <span className="small-title">
            {AppointmentUtils.getAppointmentDescription(appointment)}
          </span>
        </div>
        <div className="item-content">
          <div className="row">
            <span>{PhysicianUtils.getName(physician)}</span>
          </div>
          <div className="row">
            <span>{CalendarUtils.format(date, 'h:mm A')}</span>
          </div>
          <div className="row">
            <span>{status}</span>
          </div>
        </div>
      </div>
    </>
  );
};

const AppointmentDetailsModal = ({ setOpenModal, openModal, appointment }) =>
  openModal && (
    <DefaultModal
      className="modal appointment-details-modal"
      key="appointment-details-modal"
      wrap={true}
      padding={false}
      title="Appointment Details"
      content={
        <AppointmentDetails
          hideCalendar={true}
          allowEdit={true}
          allowClose={true}
          appointment={appointment}
          onClose={() => setOpenModal(false)}
        />
      }
      isOpen={openModal}
      onRequestClose={() => setOpenModal(false)}
    />
  );

CPPTimelineAppointment.propTypes = {
  appointment: PropTypes.object.isRequired,
  patient: PropTypes.object.isRequired,
};

function TranscriptionModal({ isOpen, transcription, onRequestClose }) {
  return (
    <DefaultModal
      isOpen={isOpen}
      onRequestClose={onRequestClose}
      content={
        <TranscriptionChat
          transcription={transcription}
          style={{ background: 'var(--color-bg-page)' }}
          appointmentId={transcription.id}
          enableAutoBackup={false}
          enableManualSave={false}
        />
      }
      wrap
      padding={false}
      title="Appointment Transcription"
    />
  );
}

function BriefingModal({ isOpen, appointment, onRequestClose }) {
  return (
    <DefaultModal
      isOpen={isOpen}
      className="medium-modal"
      onRequestClose={onRequestClose}
      content={<AppointmentBriefing className="expanded relative" appointment={appointment} />}
      wrap
      title="Carey Briefing"
    />
  );
}

function mapStateToProps(state) {
  return {
    physicians: state.physicians.byId,
  };
}

export default connect(mapStateToProps)(CPPTimelineAppointment);
